/**
 * The public SDK.
 *
 *   <script src="https://chat.example.com/sdk/chatbot.js"></script>
 *   <script>
 *     UniversalChatbot.init({ appId: 'future-pulse', apiUrl: 'https://chat.example.com/api' });
 *   </script>
 *
 * Two rules govern everything in this file, because the widget runs inside
 * somebody else's application:
 *
 *   1. Never break the host. Every entry point is wrapped, every failure is
 *      contained, and a widget that cannot start disappears instead of
 *      throwing into the host's error handler.
 *   2. Never surprise the host. Exactly one global is defined, and it is not
 *      overwritten if the name is already taken.
 */

import { ChatbotApi, ApiError } from './api';
import { ChatUi, ConnectionStatus } from './ui';
import { collectPageContext, collectScreenContext } from './pageContext';
import { navigateOnHost, pickNavigationHref } from './navigate';
import {
    clearConversation,
    loadConversation,
    saveConversation,
    StoredTurn
} from './conversationStore';
import { resolveTheme } from './theme';
import { detectMessageLanguage, resolveLanguage, strings } from './i18n';
import {
    ChatbotOptions,
    NavigationAction,
    PageContext,
    ResolvedLanguage,
    ScreenContext,
    ThemeMode,
    ThemeTokens
} from './types';

const VERSION = '1.0.0';

class ChatbotInstance {
    private readonly api: ChatbotApi;
    private ui: ChatUi | null = null;
    private sessionId: string | null = null;
    private language: ResolvedLanguage;
    private lastMessage: string | null = null;
    private destroyed = false;
    private turns: StoredTurn[] = [];
    private lastSuggestions: string[] = [];
    private title = 'Assistant';
    private titleAr = 'المساعد';
    private subtitle = '';
    private subtitleAr = '';
    private logoUrl: string | undefined;
    private themeMode: ThemeMode = 'auto';
    private themeOverrides: Partial<ThemeTokens> = {};
    private position: 'bottom-right' | 'bottom-left' = 'bottom-right';
    private maxMessageLength = 2000;
    private onlineListener: (() => void) | null = null;
    private offlineListener: (() => void) | null = null;
    private connectionStatus: ConnectionStatus = 'connecting';

    constructor(private readonly options: ChatbotOptions) {
        this.api = new ChatbotApi(options.apiUrl);
        this.language = resolveLanguage(options.language ?? 'auto');
    }

    public async start(): Promise<void> {
        const container = this.options.container ?? document.body;

        // Config is best-effort. If it fails the widget still mounts with
        // local defaults - an assistant that renders and reports a network
        // problem is far better than one that never appears.
        this.title = this.options.title ?? strings('en').title;
        this.titleAr = this.options.title ?? strings('ar').title;
        this.subtitle = this.options.subtitle ?? '';
        this.subtitleAr = this.options.subtitle ?? '';
        this.logoUrl = this.options.logoUrl;
        this.themeOverrides = {};
        this.maxMessageLength = 2000;
        this.position = this.options.position ?? 'bottom-right';
        this.themeMode = 'auto';

        try {
            const config = await this.api.getConfig(this.options.appId, this.language);
            this.setConnectionStatus('online');

            if (this.options.title === undefined) {
                this.title = config.name || this.title;
                this.titleAr = config.nameAr || config.name || this.titleAr;
            }
            if (this.options.subtitle === undefined) {
                this.subtitle = config.description ?? '';
                this.subtitleAr = config.descriptionAr ?? config.description ?? '';
            }
            if (this.options.logoUrl === undefined) {
                this.logoUrl = config.theme?.logoUrl;
            }
            this.maxMessageLength =
                config.limits?.maxMessageLength ?? this.maxMessageLength;

            if (config.theme?.mode !== undefined) {
                this.themeMode = config.theme.mode;
            }
            if (this.options.position === undefined && config.theme?.position) {
                this.position =
                    config.theme.position === 'bottom-left'
                        ? 'bottom-left'
                        : 'bottom-right';
            }
            // Server-configured colours are a deliberate operator choice, so
            // they take precedence over what the widget sniffs off the page.
            this.themeOverrides = pickThemeTokens(config.theme);
        } catch (error) {
            this.setConnectionStatus(statusFromError(error));
            reportSilently('config request failed', error);
        }

        if (typeof this.options.theme === 'string') {
            this.themeMode = this.options.theme;
        } else if (this.options.theme !== undefined) {
            this.themeOverrides = { ...this.themeOverrides, ...this.options.theme };
        }

        this.ui = new ChatUi(container, this.panelConfig(), {
            onSend: (text) => void this.send(text),
            onSuggestion: (text) => void this.send(text),
            onNavigate: (action) => this.navigate(action),
            onClear: () => this.clear(),
            onOpen: () => void this.onOpen(),
            onClose: () => this.endConversation(),
            onRetry: () => {
                if (this.lastMessage !== null) {
                    void this.send(this.lastMessage, true);
                }
            }
        });

        this.bindConnectionEvents();
        this.ui.setConnectionStatus(this.connectionStatus);

        const restored = this.restoreConversation();
        if (!restored) {
            this.ui.showEmptyState();
        }

        if (this.options.autoOpen === true || restored) {
            this.ui.open();
        }
    }

    /** Opens a session on first open, so an unopened widget costs nothing. */
    private async onOpen(): Promise<void> {
        if (this.sessionId !== null || this.ui === null) {
            return;
        }

        try {
            const session = await this.api.createSession(
                this.options.appId,
                this.options.language ?? 'auto',
                this.context()
            );

            this.sessionId = session.sessionId;
            this.language = session.language;
            this.setConnectionStatus('online');
            this.refreshLanguage();

            this.ui.clearMessages();
            const greeting = this.options.welcomeMessage ?? session.greeting;
            this.ui.addMessage('assistant', greeting);
            this.turns = [{ role: 'assistant', content: greeting }];
            this.lastSuggestions = this.options.suggestions ?? session.suggestions;
            this.ui.setSuggestions(this.lastSuggestions);
            this.persistConversation();
        } catch (error) {
            this.setConnectionStatus(statusFromError(error));
            this.showError(error);
        }
    }

    private async send(text: string, isRetry = false): Promise<void> {
        if (this.ui === null || this.destroyed) {
            return;
        }

        this.lastMessage = text;
        this.ui.hideError();

        if (!isRetry) {
            this.ui.addMessage('user', text);
            this.turns.push({ role: 'user', content: text });
            this.persistConversation();
        }
        this.lastSuggestions = [];
        this.ui.setSuggestions([]);
        this.ui.setBusy(true);

        try {
            const response = await this.api.sendMessage({
                sessionId: this.sessionId ?? undefined,
                appId: this.options.appId,
                message: text,
                // 'auto' lets the server decide, but when the host pinned a
                // language the widget still follows what the user typed, which
                // is what a bilingual user actually expects.
                language:
                    this.options.language === undefined ||
                    this.options.language === 'auto'
                        ? detectMessageLanguage(text, this.language)
                        : this.options.language,
                context: this.context()
            });

            this.sessionId = response.sessionId;
            this.setConnectionStatus('online');

            if (response.language !== this.language) {
                this.language = response.language;
                this.refreshLanguage();
            }

            this.ui.setBusy(false);
            this.lastSuggestions = response.suggestions;
            this.ui.applyResponse(response);
            this.turns.push({
                role: 'assistant',
                content: response.message.content
            });
            this.persistConversation();
        } catch (error) {
            this.ui.setBusy(false);
            this.setConnectionStatus(statusFromError(error));
            this.showError(error);
        }
    }

    private navigate(action: NavigationAction): void {
        this.persistConversation();
        const href = pickNavigationHref(action, window.location);
        const hostAction: NavigationAction =
            href === null ? action : { ...action, url: href };
        if (typeof this.options.onNavigate === 'function') {
            this.options.onNavigate(hostAction);
            return;
        }
        navigateOnHost(action);
    }

    private clear(): void {
        this.endConversation();
        void this.onOpen();
    }

    /** Drops the session so the next open starts a new conversation. */
    private endConversation(): void {
        this.sessionId = null;
        this.lastMessage = null;
        this.turns = [];
        this.lastSuggestions = [];
        clearConversation(this.options.appId);
        if (this.ui === null) {
            return;
        }
        this.ui.clearMessages();
        this.ui.hideError();
        this.ui.setSuggestions([]);
        this.ui.showEmptyState();
    }

    private persistConversation(): void {
        if (this.sessionId === null || this.turns.length === 0) {
            return;
        }
        saveConversation(this.options.appId, {
            sessionId: this.sessionId,
            language: this.language,
            turns: this.turns,
            suggestions: this.lastSuggestions
        });
    }

    /** Returns true when a previous conversation was put back on screen. */
    private restoreConversation(): boolean {
        if (this.ui === null) {
            return false;
        }
        const saved = loadConversation(this.options.appId);
        if (saved === null || saved.turns.length === 0) {
            return false;
        }
        this.sessionId = saved.sessionId;
        this.language = saved.language;
        this.turns = saved.turns;
        this.lastSuggestions = saved.suggestions ?? [];
        this.refreshLanguage();
        this.ui.replayTurns(saved.turns);
        this.ui.setSuggestions(this.lastSuggestions);
        this.setConnectionStatus('online');
        return true;
    }

    private context(): { page?: PageContext; screen?: ScreenContext } | undefined {
        if (this.options.sendPageContext === false) {
            return undefined;
        }
        const page = collectPageContext();
        const screen = collectScreenContext();
        const payload: { page?: PageContext; screen?: ScreenContext } = {};
        if (Object.keys(page).length > 0) {
            payload.page = page;
        }
        if (screen !== undefined) {
            payload.screen = screen;
        }
        return Object.keys(payload).length === 0 ? undefined : payload;
    }

    private refreshLanguage(): void {
        if (this.ui === null) {
            return;
        }
        this.ui.applyConfig(this.panelConfig());
        this.ui.setConnectionStatus(this.connectionStatus);
    }

    private panelConfig() {
        return {
            title: this.language === 'ar' ? this.titleAr : this.title,
            subtitle: this.language === 'ar' ? this.subtitleAr : this.subtitle,
            logoUrl: this.logoUrl,
            language: this.language,
            theme: resolveTheme(this.themeMode, this.themeOverrides),
            position: this.position,
            zIndex: this.options.zIndex ?? 2147483000,
            maxMessageLength: this.maxMessageLength
        };
    }

    private bindConnectionEvents(): void {
        this.unbindConnectionEvents();
        this.onlineListener = () => this.setConnectionStatus('online');
        this.offlineListener = () => this.setConnectionStatus('offline');
        window.addEventListener('online', this.onlineListener);
        window.addEventListener('offline', this.offlineListener);
        if (typeof navigator !== 'undefined' && navigator.onLine === false) {
            this.setConnectionStatus('offline');
        }
    }

    private unbindConnectionEvents(): void {
        if (this.onlineListener !== null) {
            window.removeEventListener('online', this.onlineListener);
            this.onlineListener = null;
        }
        if (this.offlineListener !== null) {
            window.removeEventListener('offline', this.offlineListener);
            this.offlineListener = null;
        }
    }

    private setConnectionStatus(status: ConnectionStatus): void {
        const resolved =
            status === 'online' &&
            typeof navigator !== 'undefined' &&
            navigator.onLine === false
                ? 'offline'
                : status;
        this.connectionStatus = resolved;
        this.ui?.setConnectionStatus(resolved);
    }

    private showError(error: unknown): void {
        if (this.ui === null) {
            return;
        }
        const copy = strings(this.language);
        const kind = error instanceof ApiError ? error.kind : 'unknown';
        this.ui.showError(copy.errors[kind] ?? copy.errors.unknown);
    }

    // ------------------------------------------------------- public surface

    public open(): void {
        this.ui?.open();
    }

    public close(): void {
        this.ui?.close();
    }

    public minimize(): void {
        this.ui?.minimize();
    }

    public toggle(): void {
        this.ui?.toggle();
    }

    public ask(text: string): void {
        this.ui?.open();
        void this.send(text);
    }

    public setLanguage(language: ResolvedLanguage): void {
        this.language = language;
        this.refreshLanguage();
    }

    public destroy(): void {
        this.destroyed = true;
        this.unbindConnectionEvents();
        this.ui?.destroy();
        this.ui = null;
    }
}

/** Copies only the colour tokens, so unrelated server fields cannot leak in. */
function pickThemeTokens(
    theme: Record<string, unknown> | undefined
): Partial<ThemeTokens> {
    if (theme === undefined || theme === null) {
        return {};
    }
    const keys: Array<keyof ThemeTokens> = [
        'primaryColor',
        'onPrimaryColor',
        'surfaceColor',
        'textColor',
        'mutedTextColor',
        'borderColor',
        'borderRadius',
        'fontFamily'
    ];

    const out: Partial<ThemeTokens> = {};
    for (const key of keys) {
        const value = theme[key];
        if (typeof value === 'string' && value.trim() !== '') {
            out[key] = value;
        }
    }
    return out;
}

function statusFromError(error: unknown): ConnectionStatus {
    if (
        error instanceof ApiError &&
        (error.kind === 'offline' || error.kind === 'timeout')
    ) {
        return 'offline';
    }
    return 'online';
}

/**
 * Logs without ever throwing.
 *
 * A host page may have removed console methods, or be running with a strict
 * CSP that makes some of them unavailable.
 */
function reportSilently(_message: string, _detail?: unknown): void {
    // Host consoles stay quiet. Failures are visible in the widget UI instead.
}

let instance: ChatbotInstance | null = null;

export const UniversalChatbot = {
    version: VERSION,

    init(options: ChatbotOptions): ChatbotInstance | null {
        try {
            if (typeof document === 'undefined') {
                return null;
            }
            if (
                options === null ||
                typeof options !== 'object' ||
                typeof options.appId !== 'string' ||
                typeof options.apiUrl !== 'string'
            ) {
                reportSilently('init requires { appId, apiUrl }');
                return null;
            }
            if (instance !== null) {
                reportSilently('already initialised; call destroy() first');
                return instance;
            }

            const created = new ChatbotInstance(options);
            instance = created;

            const boot = (): void => {
                void created.start().catch((error: unknown) => {
                    reportSilently('failed to start', error);
                });
            };

            // A script placed in <head> runs before body exists, so mounting
            // has to wait for a container to be there.
            if (document.readyState === 'loading') {
                document.addEventListener('DOMContentLoaded', boot, { once: true });
            } else {
                boot();
            }

            return created;
        } catch (error) {
            reportSilently('init threw', error);
            return null;
        }
    },

    open(): void {
        instance?.open();
    },
    close(): void {
        instance?.close();
    },
    minimize(): void {
        instance?.minimize();
    },
    toggle(): void {
        instance?.toggle();
    },
    ask(text: string): void {
        instance?.ask(text);
    },
    setLanguage(language: ResolvedLanguage): void {
        instance?.setLanguage(language);
    },
    destroy(): void {
        instance?.destroy();
        instance = null;
    }
};

declare global {
    interface Window {
        UniversalChatbot?: typeof UniversalChatbot;
    }
}

// One global, and only if the name is free. Clobbering an existing global is
// the fastest way for a third-party script to break an application.
if (typeof window !== 'undefined') {
    if (window.UniversalChatbot === undefined) {
        window.UniversalChatbot = UniversalChatbot;
    } else {
        reportSilently('window.UniversalChatbot already exists; leaving it untouched');
    }
}

export default UniversalChatbot;
